import { css } from '@emotion/react';
import {
  Button,
  ButtonGroup,
  color,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import dayjs from 'dayjs';
import { RowFlex } from '@/styles';
import { canUsePeriod, PeriodOption, PeriodOptionValue, PERIODS } from '@/config/planLimits';
import { ReactComponent as CalendarIcon } from '@/pages/dashboard/assets/calendar_solid.svg';

interface PeriodSelectorProps {
  selectedPeriod: PeriodOptionValue;
  plan: PlanType;
  onPeriodChange: (period: PeriodOptionValue) => void;
  onDateRangeClick: () => void;
  dateRange?: { startDate: string; endDate: string };
}

export default function PeriodSelector({
  selectedPeriod,
  plan,
  onPeriodChange,
  onDateRangeClick,
  dateRange
}: PeriodSelectorProps) {
  const handlePeriodClick = (period: PeriodOption) => {
    if (period.value === 'custom') {
      onDateRangeClick();
    } else {
      onPeriodChange(period.value);
    }
  };

  // 날짜 범위 포맷팅 함수
  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return `${start.format('YY/MM/DD')}~${end.format('YY/MM/DD')}`;
  };

  // custom 버튼의 표시 텍스트 결정
  const getCustomButtonLabel = (): string => {
    if (selectedPeriod === 'custom' && dateRange) {
      return formatDateRange(dateRange.startDate, dateRange.endDate);
    }
    return '기간설정';
  };

  return (
    <ButtonGroup segmented css={WrapperStyle}>
      {PERIODS.map((period) => {
        const isDisabled = !canUsePeriod(period, plan);
        const isActive = selectedPeriod === period.value;

        // 비활성화된 버튼
        if (isDisabled && period.requiredPlan !== null) {
          return (
            <Button
              type={isActive ? 'primary' : 'outlineBasic'}
              cssStyle={PeriodButtonStyle(isActive, isDisabled)}
              onClick={() => handlePeriodClick(period)}
              disabled
            >
              <div css={ButtonContentStyle}>
                {period.value === 'custom' && <CalendarIcon css={IconStyle(isActive)} />}
                <Typography type="button" cssStyle={TextStyle(isActive, isDisabled)}>
                  {period.value === 'custom' ? getCustomButtonLabel() : period.label}
                </Typography>
              </div>
            </Button>
          );
        }

        return (
          <Button
            key={period.value}
            type={isActive ? 'primary' : 'outlineBasic'}
            cssStyle={PeriodButtonStyle(isActive, isDisabled)}
            onClick={() => handlePeriodClick(period)}
          >
            <div css={ButtonContentStyle}>
              {period.value === 'custom' && <CalendarIcon css={IconStyle(isActive)} />}
              <Typography type="button" cssStyle={TextStyle(isActive, isDisabled)}>
                {period.value === 'custom' ? getCustomButtonLabel() : period.label}
              </Typography>
            </div>
          </Button>
        );
      })}
    </ButtonGroup>
  );
}

const TextStyle = (isActive: boolean, isDisabled = false) => css`
  color: ${getTextColor(isActive, isDisabled)};
`;

const ButtonContentStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconStyle = (isActive: boolean) => css`
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  path {
    fill: ${isActive ? '#ffffff' : '#555555'};
  }
`;

function getTextColor(isActive: boolean, isDisabled: boolean) {
  if (isDisabled) {
    return color.text_disabled;
  }

  if (isActive) {
    return '#ffffff';
  }

  return color.text_default;
}

const WrapperStyle = css`
  ${RowFlex}
  align-items: center;
  gap: 0;
  padding: 4px;
  background-color: ${color.surface_default_subdued};
  border-radius: ${spacing.borderRadius.medium};
  width: fit-content;
  max-width: 600px;
  height: 36px;
`;

const PeriodButtonStyle = (isActive: boolean, isDisabled = false) => css`
  padding: 8px 16px;
  background-color: ${getButtonBackground(isActive, isDisabled)};
  border-radius: ${spacing.borderRadius.small};
  transition: background-color 0.2s ease;
  width: fit-content;
  opacity: ${isDisabled ? 0.5 : 1};
  position: relative;

  &:hover {
    background-color: ${getButtonHoverBackground(isActive)};
  }

  ${isDisabled &&
  css`
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background-color: transparent;
      cursor: default;
    }
  `}
`;

function getButtonBackground(isActive: boolean, isDisabled: boolean) {
  if (isActive) {
    return color.palette_primary_purple_100;
  }

  if (isDisabled) {
    return color.surface_default_subdued;
  }

  return 'transparent';
}

function getButtonHoverBackground(isActive: boolean) {
  if (isActive) {
    return color.palette_primary_purple_100;
  }

  return color.surface_default_hovered;
}
